import React, { useEffect, useState, useMemo } from 'react';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import GuidedUpsell from '../../components/GuidedUpsell';
import SubmitEventModal from '../../components/SubmitEventModal';
import ViatorCtaCard from '../../components/ViatorCtaCard';
import { useAuth } from '../../context/AuthContext';
import { Calendar, MapPin, Clock, Users, ExternalLink, CalendarPlus, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Iter 50: simplified filters - In-Person/Online toggle + City dropdown.
const FALLBACK_CITIES = [
  'Mexico City', 'Tulum', 'Puerto Vallarta', 'Oaxaca',
  'San Miguel de Allende', 'Mérida', 'Guadalajara', 'Cabo San Lucas',
];
const MODE_FILTERS = ['All', 'In-Person', 'Online'];

const matchesMode = (event, mode) => {
  if (mode === 'All') return true;
  const isOnline = event.type === 'Virtual'
    || (event.location || '').toLowerCase().match(/online|zoom|virtual/);
  if (mode === 'Online') return !!isOnline;
  if (mode === 'In-Person') return !isOnline;
  return true;
};

const matchesCity = (event, city) => {
  if (!city) return true;
  const loc = (event.location || '').toLowerCase();
  return loc.includes(city.toLowerCase());
};

const FilterChip = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-1.5 rounded-full border transition ${
      active
        ? 'bg-[#0A1F2E] text-white border-[#0A1F2E]'
        : 'bg-white text-[#0A1F2E]/75 border-[#EFE6CF] hover:border-[#0A1F2E]/40'
    }`}
  >
    {label}
  </button>
);

const EventCard = ({ event, onIcs }) => (
  <article
    key={event.id}
    className="bg-white border border-[#EFE6CF] rounded-xl p-6 hover:border-[#2CA8A0]/40 transition"
    data-testid={`event-${event.id}`}
  >
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className={`font-mono uppercase text-[9px] tracking-[0.15em] px-2 py-0.5 rounded ${
            event.type === 'Virtual'
              ? 'bg-[#2CA8A0]/15 text-[#1A5C6E]'
              : 'bg-[#E8A830]/15 text-[#8a5d12]'
          }`}>
            {event.type}
          </span>
          {event.category && (
            <span className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#0A1F2E]/55">
              {event.category}
            </span>
          )}
        </div>
        <h3 className="font-display italic text-xl md:text-2xl text-[#0A1F2E] mb-2 leading-tight">
          {event.title}
        </h3>
        {event.summary && (
          <p className="font-body text-sm text-[#0A1F2E]/70 mb-4 leading-relaxed">
            {event.summary}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 text-sm text-[#0A1F2E]/75">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#1A5C6E]" />
            {event.when_display}
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#1A5C6E]" />
            {event.duration_minutes} min
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#1A5C6E]" />
            {event.location}
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#1A5C6E]" />
            {event.attendees ?? 0} attending
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 md:items-end shrink-0">
        <button
          type="button"
          onClick={() => onIcs(event.id, event.title)}
          className="inline-flex items-center gap-2 bg-[#E8A830] text-[#0A1F2E] px-4 py-2 text-sm font-semibold hover:brightness-95 transition rounded-full font-mono uppercase tracking-[0.12em]"
          data-testid={`event-${event.id}-ics`}
          aria-label={`Add ${event.title} to calendar`}
        >
          <CalendarPlus className="w-4 h-4" />
          Calendar
        </button>
        {event.url && (
          <a
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[#0A1F2E]/15 text-[#0A1F2E] px-4 py-2 text-sm hover:bg-[#F4ECD8] transition rounded-full font-mono uppercase tracking-[0.12em]"
            data-testid={`event-${event.id}-rsvp`}
          >
            RSVP
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  </article>
);

const PortalEvents = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('All');
  const [city, setCity] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/events`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { events: [] }))
      .then((d) => setEvents(d.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  // Auto-discover cities from the actual event data; fall back to the
  // canonical Mundalo list when the events feed is empty.
  const cityOptions = useMemo(() => {
    const found = new Set();
    for (const e of events) {
      const loc = (e.location || '').split(',')[0].trim();
      if (loc && loc.toLowerCase() !== 'online' && loc.toLowerCase() !== 'virtual') {
        found.add(loc);
      }
    }
    const dynamic = [...found].sort();
    return dynamic.length ? dynamic : FALLBACK_CITIES;
  }, [events]);

  const downloadIcs = (eventId, title) => {
    const link = document.createElement('a');
    link.href = `${API_URL}/api/events/${eventId}/ics`;
    link.download = `${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filtered = useMemo(
    () => events.filter((e) => matchesMode(e, mode) && matchesCity(e, city)),
    [events, mode, city]
  );

  const [submitOpen, setSubmitOpen] = useState(false);

  const reload = async () => {
    try {
      const r = await fetch(`${API_URL}/api/events`);
      if (r.ok) {
        const d = await r.json();
        setEvents(d.events || []);
      }
    } catch { /* noop */ }
  };

  return (
    <PortalLayout activeTab="events">
      <SEO title="Events - Mundalo" description="Upcoming Mundalo member events." path="/portal/events" />

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="portal-events-page">
        <GuidedUpsell variant="card" storageKey="events" />

        <header className="mb-8 sm:flex sm:items-end sm:justify-between gap-6">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
              The Life · Events
            </p>
            <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
              Upcoming events.
            </h1>
            <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
              Meetups, Q&amp;A sessions, and city tours for Mundalo members.
            </p>
          </div>
          {/* iter 142c - open submissions to ALL logged-in users (no tier gate).
              The /api/events/submit endpoint already authenticates by JWT;
              admin moderation still required before publish. */}
          {user && (
            <button
              type="button"
              onClick={() => setSubmitOpen(true)}
              className="mt-4 sm:mt-0 inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-3 rounded-full transition shrink-0"
              data-testid="submit-event-cta"
            >
              <Plus className="w-4 h-4" /> Submit an event
            </button>
          )}
        </header>

        {/* === In-Person / Online toggle + City dropdown === */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8" data-testid="events-filters">
          <div className="flex flex-wrap gap-2" data-testid="events-mode-chips">
            {MODE_FILTERS.map((m) => (
              <FilterChip
                key={m}
                label={m}
                active={mode === m}
                onClick={() => setMode(m)}
                testId={`events-mode-${m.toLowerCase().replace(/\s+/g, '-')}`}
              />
            ))}
          </div>
          <div className="sm:ml-auto">
            <label htmlFor="events-city-select" className="sr-only">City</label>
            <select
              id="events-city-select"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="events-city-select"
              className="w-full sm:w-auto bg-white border border-[#EFE6CF] text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-2 rounded-full hover:border-[#0A1F2E]/40 focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
            >
              <option value="">All cities</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-[#F4ECD8] border border-[#EFE6CF] h-40 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="events-empty-state" className="space-y-6">
            <p className="font-body text-base text-[#0A1F2E]/55 text-center py-6">
              {events.length === 0
                ? 'No upcoming events yet - check back soon.'
                : 'No events match the selected filters.'}
            </p>
            {/* Viator affiliate - events empty-state CTA (iter 77).
                Surfaced whenever events list is empty (no feed or filtered to zero). */}
            <ViatorCtaCard
              eyebrow="Things to do"
              headline="No events yet - but there’s always something to do."
              subtext="Discover handpicked tours and experiences for your new city."
              buttonLabel="Find Experiences on Viator →"
              testId="viator-cta-events-empty"
              surface="events_empty"
              cityName={city || null}
              userRole={user?.role || null}
            />
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((event) => (
              <EventCard key={event.id} event={event} onIcs={downloadIcs} />
            ))}
          </div>
        )}
      </div>

      <SubmitEventModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmitted={reload}
      />
    </PortalLayout>
  );
};

export default PortalEvents;
